import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isConnectionOwned } from '@/components/OwnedChip'
import { useViewOnly } from '@/components/templates/app-shell'
import { EntityForm, FormField } from '@/components/templates/entity-form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { getJSON, patchJSON, postJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import type { CourseResponse } from '@/pages/CourseDetailPage'

interface Props {
  courseId?: number
  onSaved: (courseId: number) => void
  onCancel: () => void
}

export function CourseFormPage(props: Props) {
  const viewOnly = useViewOnly()
  if (viewOnly) return <p className="p-6">You do not have permission to change courses.</p>
  return <CourseFormLoader {...props} />
}

function CourseFormLoader(props: Props) {
  const query = useQuery({
    queryKey: ['course-form', props.courseId],
    queryFn: async () => {
      if (props.courseId) return getJSON<CourseResponse>(`/api/v1/courses/${props.courseId}`)
      const settings = await getJSON<{ credit_unit_settings: { label: string } }>(
        '/api/v1/credit_unit_settings',
      )
      return {
        course: {
          title: '',
          description: '',
          delivered_by: '',
          link: '',
          credits: '0',
          credit_unit_label: settings.credit_unit_settings.label,
          owner: null,
        },
      }
    },
  })
  if (query.isPending) return <p className="p-6">Loading course form…</p>
  if (query.isError)
    return (
      <p role="alert" className="p-6">
        {query.error.message}
      </p>
    )
  if (isConnectionOwned(query.data.course.owner))
    return <p className="p-6">This course is managed by its connection.</p>
  return <CourseForm {...props} initial={query.data.course} />
}

type InitialCourse = Pick<
  CourseResponse['course'],
  'title' | 'description' | 'delivered_by' | 'link' | 'credits' | 'credit_unit_label'
>
const fields = [
  { name: 'title', label: 'Title' },
  { name: 'description', label: 'Description' },
  { name: 'delivered_by', label: 'Delivered by' },
  { name: 'link', label: 'Link' },
  { name: 'credits', label: 'Credits' },
] as const

function CourseForm({ initial, ...props }: Props & { initial: InitialCourse }) {
  const [idempotencyKey] = useState(() => crypto.randomUUID())
  const client = useQueryClient()
  const mutation = useMutation({
    mutationFn: (course: Record<string, FormDataEntryValue>) =>
      props.courseId
        ? patchJSON<CourseResponse>(`/api/v1/courses/${props.courseId}`, { course })
        : postJSON<CourseResponse>('/api/v1/courses', {
            course: { ...course, idempotency_key: idempotencyKey },
          }),
    onSuccess: ({ course }) => {
      client.setQueryData(['course', course.id], { course })
      void client.invalidateQueries({ queryKey: ['courses'] })
      void client.invalidateQueries({ queryKey: ['course-form'] })
      props.onSaved(course.id)
    },
  })
  return (
    <EntityForm
      className="mx-0"
      title={props.courseId ? 'Edit course' : 'New course'}
      submitLabel={props.courseId ? 'Save changes' : 'Create course'}
      onCancel={props.onCancel}
      submitting={mutation.isPending}
      error={mutation.error?.message}
      onSubmit={(event) => {
        event.preventDefault()
        if (!mutation.isPending)
          mutation.mutate(Object.fromEntries(new FormData(event.currentTarget)))
      }}
    >
      {fields.map(({ name, label }) => (
        <FormField
          key={name}
          htmlFor={name}
          label={name === 'credits' ? `Credits (${initial.credit_unit_label})` : label}
          required={name === 'title'}
          error={apiFieldMessages(mutation.error, name).join(' ')}
        >
          <CourseInput name={name} initial={initial} />
        </FormField>
      ))}
    </EntityForm>
  )
}

function CourseInput({
  name,
  initial,
}: {
  name: (typeof fields)[number]['name']
  initial: InitialCourse
}) {
  const shared = { id: name, name, defaultValue: initial[name] ?? '' }
  if (name === 'description') return <Textarea {...shared} />
  if (name === 'credits') return <Input {...shared} type="number" min="0" step="any" />
  return <Input {...shared} required={name === 'title'} />
}
